import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Company } from '@backModels/Ref.Company';
import { CompanyListItem } from '@backModels/Models.CompanyListItem';
import { CompaniesApi } from '@shared/api/companies.api';
import { FilesApi } from '@shared/api/files.api';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  company = signal<Company | undefined>(undefined);
  companyGuid = signal<string | undefined>(undefined);
  companies = signal<CompanyListItem[]>([]);
  isotypeMap = signal<Map<string, string>>(new Map());
  private companiesApi = inject(CompaniesApi);
  private filesApi = inject(FilesApi);

  async loadCompany(guid: string): Promise<void> {
    this.company.set(undefined);
    const company = await this.companiesApi.getCompany(guid);
    this.company.set(company);
  }

  /** Resolves signed isotype URLs for companies missing from isotypeMap and merges them in.
   *  Group companies may not be in the user's accessible list, so the map built by the
   *  companies screen does not necessarily cover them. */
  async ensureIsotypes(companies: { companyGuid: string; isotypeImageGuid?: string }[]): Promise<void> {
    const missing = companies.filter(c => c.isotypeImageGuid && !this.isotypeMap().has(c.companyGuid));
    if (!missing.length) return;
    const entries = await Promise.all(
      missing.map(async c => {
        try {
          const url = await firstValueFrom(this.filesApi.getDownloadUri(c.isotypeImageGuid!));
          return url ? ([c.companyGuid, url] as const) : null;
        } catch {
          // Un isotipo que no resuelve no debe romper el select — se muestra el icono genérico.
          return null;
        }
      }),
    );
    const map = new Map(this.isotypeMap());
    for (const entry of entries) {
      if (entry) map.set(entry[0], entry[1]);
    }
    this.isotypeMap.set(map);
  }
}
